import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PoweredByMeitre } from '@shared/components/common';
import { ChevronLeft, Mail, User, Lock } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Checkbox } from '@shared/components/ui/checkbox';
import { Input } from '@shared/components/ui/input';
import { api, ApiError } from '@core/api';
import { getCurrentLanguage } from '@core/i18n';
import { useGoogleSignIn } from '../hooks/useGoogleSignIn';
import { useFacebookLogin } from '../hooks/useFacebookLogin';
import { PhoneInput } from '@shared/components/common/PhoneInput';
import { Private } from '@shared/components/common/Private';
import { isPhoneEmpty, isValidPhone } from '@shared/utils/phoneValidation';
import { detectInAppBrowser } from '@shared/utils/inAppBrowser';
import { redirectWithToken as performAuthRedirectWithToken } from '../utils/redirectWithToken';
import {
  buildOAuthSignupPath,
  buildLoginPathFromOAuthBack,
  readOAuthPrefillHandle,
  type OAuthSignupLocationState,
} from '../utils/oauthSignupUrl';
import {
  putOAuthPrefill,
  peekOAuthPrefill,
  clearOAuthPrefill,
} from '../utils/oauthPrefillStore';
import type { OAuthPrefill, OAuthProvider } from '../types';

interface SignupResponse {
  token: string;
  email: string;
  userId?: string;
}

/**
 * Standalone signup page for auth.v2.meitre.com.
 *
 * Reads context from URL params: return_url, restaurant, lang.
 * After successful signup, auto-logs in and redirects to return_url.
 */
export function AuthSignupPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const returnUrl = searchParams.get('return_url') || '';
  const restaurant = searchParams.get('restaurant') || '';
  const country = searchParams.get('country') || '';
  const dialCode = searchParams.get('dial_code') || '';
  const { isInAppBrowser } = detectInAppBrowser();

  // OAuth prefill is delivered as an opaque handle via React Router's
  // `location.state`. The actual payload lives in an in-memory module
  // store (see oauthPrefillStore.ts) and never travels through the URL,
  // history.state, or any persisted storage. The handle is useless without
  // that process's live memory, so a full reload (which discards the
  // module state) naturally falls back to the landing screen — the right
  // outcome vs persisting a credential-equivalent uid.
  const prefillEnvelope = useMemo(
    () => readOAuthPrefillHandle(location.state),
    [location.state],
  );
  const prefillHandle = prefillEnvelope?.handle ?? null;
  const prefillOrigin = prefillEnvelope?.origin ?? null;
  const prefill = useMemo(
    () => peekOAuthPrefill(prefillHandle),
    [prefillHandle],
  );
  const prefillActive = prefill !== null;

  /** Redirect back to the restaurant app with auth credentials */
  const redirectWithToken = useCallback(
    async (token: string, email: string, initialUserId = '') => {
      await performAuthRedirectWithToken({
        token,
        email,
        restaurant,
        returnUrl,
        initialUserId,
        successMessage: t('SIGNUP_MESSAGE_1'),
      });
    },
    [returnUrl, restaurant, t],
  );

  /**
   * Re-land on /signup with the OAuth handle attached via location.state.
   * `replace: true` collapses the intermediate /signup entry so Back skips
   * past the in-progress OAuth attempt instead of retriggering it.
   */
  const handleNeedsSignup = useCallback(
    (nextPrefill: OAuthPrefill) => {
      const state: OAuthSignupLocationState = {
        prefillHandle: putOAuthPrefill(nextPrefill),
        origin: 'signup',
      };
      navigate(buildOAuthSignupPath(searchParams), { state, replace: true });
    },
    [navigate, searchParams],
  );

  const google = useGoogleSignIn({
    onSuccess: redirectWithToken,
    onNeedsSignup: handleNeedsSignup,
  });

  const facebook = useFacebookLogin({
    onSuccess: redirectWithToken,
    onNeedsSignup: handleNeedsSignup,
  });

  const [showForm, setShowForm] = useState(prefillActive);
  const [name, setName] = useState(prefill?.fullname ?? '');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(prefill?.email ?? '');
  const [password, setPassword] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Keep name / email / showForm in sync when the prefill appears after the
  // initial mount. Happens when the user is already on /signup, clicks
  // "Continue with Google", the OAuth hook reports needs-signup, and the
  // page re-renders at the same route with a new query string (React Router
  // does not remount on query-only navigation, so useState initial values
  // from the previous URL persist without this).
  useEffect(() => {
    if (!prefill) return;
    setEmail(prefill.email);
    setName(prefill.fullname);
    setShowForm(true);
    // Clear any stale password the user may have typed in a previous
    // non-prefill attempt on this mount — the submit path will use
    // prefill.uid instead.
    setPassword('');
    setError(null);
  }, [prefill]);

  // Browser Back / Forward cleanup. Custom exit handlers (handleBack,
  // handleGoToLogin, successful submit) already call clearOAuthPrefill,
  // but if the user uses the browser's native back button we never run.
  // Without this, the handle stays in the module store and a Forward
  // within the 10-minute TTL would revive the form with the abandoned
  // credential-equivalent uid. The `popstate` listener catches that
  // navigation and releases the entry; the handler does not call
  // preventDefault, so it can be registered as passive.
  useEffect(() => {
    if (!prefillHandle) return;
    const release = () => clearOAuthPrefill(prefillHandle);
    window.addEventListener('popstate', release, { passive: true });
    return () => {
      window.removeEventListener('popstate', release);
    };
  }, [prefillHandle]);

  // Path to /login used by the "Already have an account? Log in" link.
  // Built with the back-aware helper so the `provider` auto-trigger hint
  // does not ride along — arriving at /login from here should show the
  // landing screen, never reopen the OAuth popup the user just abandoned.
  const loginPath = useMemo(
    () => buildLoginPathFromOAuthBack(searchParams),
    [searchParams],
  );

  /**
   * User-initiated exit from the prefilled signup. Discard the in-memory
   * payload so browser Forward (or another device user) cannot revive the
   * form with the abandoned OAuth uid still populated. Destination depends
   * on where the OAuth attempt originated — login page goes to /login;
   * signup page returns to the /signup landing screen.
   *
   * When the destination is the same route (/signup → /signup landing),
   * React Router does NOT remount the component. We reset the local form
   * state explicitly so the user does not see the regular email signup
   * form still populated with the OAuth-derived name/email.
   */
  const exitPrefill = useCallback(() => {
    clearOAuthPrefill(prefillHandle);
    if (prefillOrigin === 'signup') {
      setShowForm(false);
      setName('');
      setEmail('');
      setPassword('');
      setError(null);
      setPhoneError(null);
      navigate(buildOAuthSignupPath(searchParams), { replace: true });
      return;
    }
    navigate(buildLoginPathFromOAuthBack(searchParams));
  }, [navigate, prefillHandle, prefillOrigin, searchParams]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setPhoneError(null);

      // Re-peek the store at submit time. The prefill returned by useMemo
      // snapshotted the payload on first render; if the tab has been open
      // past the 10-minute TTL the store has since evicted the entry, and
      // we must not let the user submit with a uid the store considers
      // expired. Treat an expired handle as "prefill is gone", ask the
      // user to restart the OAuth flow, and scrub the stale form state.
      const freshPrefill = prefillActive
        ? peekOAuthPrefill(prefillHandle)
        : null;
      if (prefillActive && !freshPrefill) {
        setError(t('SIGNUP_REGISTRATION_ERROR'));
        setIsSubmitting(false);
        exitPrefill();
        return;
      }

      // On the OAuth prefill path the user never sees a password field.
      // Backend stores `uid` as the hashed password AND as `googleID` /
      // `facebookID` (see meitre-backend UserController.php:634-636), so
      // future OAuth logins match via `findOneByEmail`. Email is also
      // guaranteed-present from the prefill reader.
      // All three identity fields (email, password-as-uid, provider) must
      // come from the same source. Reading `email` from local state while
      // using `freshPrefill.uid` as password would let a race (e.g. a
      // second needs-signup arrival with a different email between first
      // paint and submit) link the OAuth credential to the wrong address,
      // bypassing the provider's email verification.
      const effectiveEmail = freshPrefill ? freshPrefill.email : email;
      const effectivePassword = freshPrefill ? freshPrefill.uid : password;
      const effectiveProvider = freshPrefill?.provider ?? '';

      const needsEmail = !freshPrefill;
      const needsPassword = !freshPrefill;
      if (!name.trim() || (needsEmail && !effectiveEmail.trim()) || (needsPassword && !password.trim())) {
        setError(t('FORM_ERROR_FILL_REQUIRED'));
        return;
      }

      if (isPhoneEmpty(phone)) {
        setPhoneError(t('FORM_ERROR_TYPE_PHONE'));
        return;
      }

      if (!isValidPhone(phone)) {
        setPhoneError(t('FORM_ERROR_INVALID_PHONE'));
        return;
      }

      setIsSubmitting(true);

      try {
        const lang = getCurrentLanguage();
        const result = await api.post<SignupResponse>(
          `/api/${lang}/register-and-login`,
          {
            username: effectiveEmail.trim(),
            name: name.trim(),
            password: effectivePassword,
            phone: phone.trim(),
            restaurant,
            optInEmail: marketingConsent,
            provider: effectiveProvider,
            is18Year: true,
          },
          { skipAuth: true },
        );

        // Clear the in-memory prefill before we navigate away — the uid
        // is no longer needed (the backend already accepted it as the
        // account password) and the store keeps one less credential-
        // equivalent value in memory.
        clearOAuthPrefill(prefillHandle);
        await redirectWithToken(result.token, result.email, result.userId || '');
      } catch (err) {
        console.error('[AuthSignup] Error:', err);
        if (err instanceof ApiError && typeof err.serverCode === 'string') {
          const key = 'SIGNUP_ERROR_' + err.serverCode.toUpperCase();
          setError(t(key, { defaultValue: t('SIGNUP_REGISTRATION_ERROR') }));
        } else {
          setError(t('SIGNUP_REGISTRATION_ERROR'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [name, email, password, phone, restaurant, marketingConsent, redirectWithToken, t, prefillActive, prefillHandle, exitPrefill],
  );

  const handleBack = useCallback(() => {
    // In the OAuth prefill flow the landing screen does not belong to this
    // journey — the user came from /login or /signup via an OAuth "user
    // not found" response. Route back to the right page with the prefill
    // cleared.
    if (prefillActive) {
      exitPrefill();
      return;
    }
    if (showForm) {
      setShowForm(false);
      return;
    }
    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      navigate(-1);
    }
  }, [prefillActive, showForm, returnUrl, navigate, exitPrefill]);

  /**
   * "Already have an account? Log in" click. When the signup is prefilled
   * this is an abandon path, same as Back, so wipe the payload. When it's
   * the email signup path (no prefill), the handle is null and
   * `clearOAuthPrefill` is a no-op — safe to call unconditionally.
   */
  const handleGoToLogin = useCallback(() => {
    clearOAuthPrefill(prefillHandle);
    navigate(loginPath);
  }, [navigate, loginPath, prefillHandle]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-0 lg:p-4 bg-background">
      <div className="w-full lg:max-w-md bg-card shadow-none rounded-b-2xl lg:shadow-lg lg:rounded-2xl overflow-visible lg:overflow-hidden animate-fade-in border-t border-border/30">
        <main className="px-6 pt-8 pb-10 flex flex-col items-center">
          {!showForm ? (
            <div className="w-full max-w-xs flex flex-col items-center">
              <h1 className="text-3xl font-display mb-2 text-center">{t('SIGNUP_FORM_TITLE')}</h1>
              <p className="text-foreground/50 text-sm mb-8 text-center">{t('SIGNUP_FORM_SUBTITLE')}</p>

              <div className="w-full flex flex-col gap-3 mb-10">
                {!isInAppBrowser && (
                  <>
                    {/* Google Sign-Up */}
                    <Button
                      variant="outline"
                      className="py-6 flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02]"
                      onClick={google.signIn}
                      disabled={google.isLoading || !google.isReady}
                      type="button"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                      {t('SIGNIN_CONTINUE_WITH_GOOGLE')}
                    </Button>

                    {/* Facebook Sign-Up */}
                    <Button
                      variant="outline"
                      className="py-6 flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02]"
                      onClick={facebook.signIn}
                      disabled={facebook.isLoading || !facebook.isReady}
                      type="button"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                      {t('SIGNUP_CONTINUE_WITH_FACEBOOK')}
                    </Button>

                    {/* Divider */}
                    <div className="flex items-center gap-3 my-1">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-foreground/40 text-xs uppercase">{t('SIGNUP_OR')}</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                  </>
                )}

                {/* Email Sign-Up */}
                <Button
                  className="py-6 flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02]"
                  onClick={() => setShowForm(true)}
                  type="button"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  {t('SIGNUP_LABEL_SUBMIT')}
                </Button>
              </div>

              {!isInAppBrowser && (google.error || facebook.error) && (
                <p className="text-red-500 text-sm text-center mb-4">
                  {google.error || facebook.error}
                </p>
              )}

              <div className="w-full text-center">
                <span className="text-foreground/70 text-sm">
                  {t('NEW_MODAL_ALREADY_HAVE_ACCOUNT')}{' '}
                  <button
                    type="button"
                    className="text-brand font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 rounded"
                    onClick={handleGoToLogin}
                  >
                    {t('SIGNUP_LOG_IN')}
                  </button>
                </span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="w-full max-w-xs flex flex-col gap-5">
              <h1 className="text-3xl font-display mb-1 text-center">
                {prefillActive ? t('SIGNUP_OAUTH_TITLE') : t('SIGNUP_FORM_TITLE')}
              </h1>
              <p className="text-foreground/50 text-sm mb-2 text-center">
                {prefillActive ? t('SIGNUP_OAUTH_SUBTITLE') : t('SIGNUP_FORM_SUBTITLE')}
              </p>

              {prefillActive && prefill && (
                <OAuthProviderBadge provider={prefill.provider} t={t} />
              )}

              {/* Name / phone / email / password are all PII. Wrap the
                  fieldset region in <Private> so session-replay tools
                  never capture signup form values. `asContents` keeps the
                  children as direct flex items of the parent <form
                  className="flex flex-col gap-5">, preserving the original
                  20px vertical rhythm. */}
              <Private asContents>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" size={20} />
                <Input
                  className="pl-10 py-6"
                  placeholder={t('FORM_LABEL_NAME')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                  type="text"
                />
              </div>

              <PhoneInput
                value={phone}
                onChange={(v) => {
                  setPhone(v);
                  if (phoneError) {
                    const empty = isPhoneEmpty(v);
                    if (empty) setPhoneError(t('FORM_ERROR_TYPE_PHONE'));
                    else if (!isValidPhone(v)) setPhoneError(t('FORM_ERROR_INVALID_PHONE'));
                    else setPhoneError(null);
                  }
                }}
                defaultCountry={country}
                dialCode={dialCode}
                className={`rounded-md border bg-background px-3 py-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${phoneError ? 'border-red-500' : 'border-input'}`}
              />

              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" size={20} />
                <Input
                  className={`pl-10 py-6 ${prefillActive ? 'bg-foreground/5 text-foreground/60 cursor-not-allowed' : ''}`}
                  placeholder={t('SIGNUP_LABEL_EMAIL')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  type="email"
                  readOnly={prefillActive}
                  aria-readonly={prefillActive}
                />
              </div>

              {/* Password is only collected on the email signup path. The
                  OAuth prefill path uses the provider UID as the backend
                  password (see meitre-backend UserController.php:634-636),
                  so the field stays hidden. */}
              {!prefillActive && (
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" size={20} />
                  <Input
                    className="pl-10 py-6"
                    placeholder={t('SIGNUP_LABEL_PASSWORD')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    type="password"
                  />
                </div>
              )}
              </Private>

              <label className="flex items-start gap-3 text-foreground/70 text-sm mt-1 mb-2 cursor-pointer select-none">
                <Checkbox
                  checked={marketingConsent}
                  onCheckedChange={() => setMarketingConsent((v) => !v)}
                  className="mt-0.5 rounded"
                />
                <span>{t('NEW_MODAL_AGREE_RESTAURANT_SEND_OCCASIONAL_INFO')}</span>
              </label>

              {(error || phoneError) && (
                <p className="text-red-500 text-sm text-center">{error || phoneError}</p>
              )}

              <Button
                type="submit"
                className="h-12 px-8 rounded-xl shadow-md flex items-center justify-center gap-2 w-full transition-all duration-200 hover:scale-[1.02]"
                disabled={isSubmitting}
              >
                {!prefillActive && <Mail className="w-5 h-5 mr-2" />}
                {isSubmitting
                  ? t('MAGIC_LINK_SENDING')
                  : prefillActive
                    ? t('SIGNUP_OAUTH_COMPLETE_BUTTON')
                    : t('SIGNUP_LABEL_SUBMIT')}
              </Button>

              <div className="w-full text-center mt-3">
                <span className="text-foreground/70 text-sm">
                  {t('NEW_MODAL_ALREADY_HAVE_ACCOUNT')}{' '}
                  <button
                    type="button"
                    className="text-brand font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 rounded"
                    onClick={handleGoToLogin}
                  >
                    {t('SIGNUP_LOG_IN')}
                  </button>
                </span>
              </div>
            </form>
          )}
        </main>

        <footer className="border-t border-border px-6 py-4 flex items-center">
          <button
            aria-label="Go back"
            className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
            onClick={handleBack}
            type="button"
          >
            <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
          </button>
        </footer>
      </div>

      <p className="hidden lg:block mt-6 text-xs">
        <PoweredByMeitre />
      </p>
    </div>
  );
}

export default AuthSignupPage;

/**
 * i18n key for each provider's badge label. Centralized so adding a new
 * provider touches one object instead of a conditional chain.
 */
const PROVIDER_BADGE_KEYS: Record<OAuthProvider, string> = {
  google: 'SIGNUP_OAUTH_BADGE_GOOGLE',
  facebook: 'SIGNUP_OAUTH_BADGE_FACEBOOK',
};

/**
 * Small visual badge shown above the OAuth signup form to confirm which
 * provider the user just authenticated with. Keeps the icons co-located with
 * the page that uses them so they tree-shake together and we don't need a
 * separate shared component for a single caller.
 */
function OAuthProviderBadge({
  provider,
  t,
}: {
  provider: OAuthProvider;
  t: (key: string) => string;
}) {
  const label = t(PROVIDER_BADGE_KEYS[provider]);

  return (
    <div className="flex items-center justify-center gap-2 rounded-lg border border-border bg-foreground/5 px-3 py-2 text-foreground/80 text-sm">
      {provider === 'google' ? (
        <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
      ) : (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      )}
      <span>{label}</span>
    </div>
  );
}
